import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconLuk } from "../../components/icons";
import { postFejl, type FejlKategori, type ProduktDto } from "../../lib/api";
import { useDialog } from "../../lib/useDialog";

const KATEGORIER: FejlKategori[] = ["LinkVirkerIkke", "LinkForkertProdukt", "PrisVilkaarForkert", "Andet"];

/** Popup for reporting a problem with a product (dead link, wrong product, …).
 * Reports are stored anonymously and triaged in the backoffice. */
export default function FejlModal({ produkt, onClose }: { produkt: ProduktDto; onClose: () => void }) {
  const { t } = useTranslation();
  const [kategori, setKategori] = useState<FejlKategori>("LinkVirkerIkke");
  const [kommentar, setKommentar] = useState("");
  const [state, setState] = useState<"form" | "sending" | "sent" | "error">("form");
  const dialogRef = useDialog<HTMLDivElement>(onClose);
  const kvitteringRef = useRef<HTMLButtonElement>(null);

  const send = async () => {
    setState("sending");
    const ok = await postFejl(produkt.produktversionId, kategori, kommentar);
    setState(ok ? "sent" : "error");
  };

  // Naar formularen er sendt, forsvinder knappen der havde fokus. Uden det her
  // falder fokus tilbage til body, og tastaturbrugeren staar uden holdepunkt.
  useEffect(() => {
    if (state === "sent") kvitteringRef.current?.focus();
  }, [state]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t("fejl.title")}
        className="w-full max-w-md rounded-l border border-line bg-surface p-6 shadow-lift outline-none"
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl">{t("fejl.title")}</h3>
          <button type="button" aria-label={t("fejl.close")} className="btn btn-ghost -mr-2 -mt-1 px-2" onClick={onClose}>
            <IconLuk className="h-[17px] w-[17px]" />
          </button>
        </div>
        <p className="mt-1 text-[13px] text-ink-soft">
          {produkt.udbyder} {produkt.produkt}
        </p>

        {state === "sent" ? (
          <div className="mt-5">
            <p className="text-[15px] font-semibold">{t("fejl.tak")}</p>
            <p className="mt-1 text-[13px] text-ink-soft">{t("fejl.takSub")}</p>
            <button ref={kvitteringRef} type="button" className="btn btn-primary mt-4" onClick={onClose}>{t("fejl.close")}</button>
          </div>
        ) : (
          <>
            <fieldset className="mt-4 flex flex-col gap-2">
              <legend className="sr-only">{t("fejl.title")}</legend>
              {KATEGORIER.map((k) => (
                <label key={k} className="flex cursor-pointer items-center gap-2.5 rounded-m border border-line px-3 py-2.5 text-sm has-[:checked]:border-ink">
                  <input
                    type="radio"
                    name="fejl-kategori"
                    className="accent-[var(--accent)]"
                    checked={kategori === k}
                    onChange={() => setKategori(k)}
                  />
                  {t(`fejl.kat.${k}`)}
                </label>
              ))}
            </fieldset>

            <label className="mt-3 flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">{t("fejl.kommentar")}</span>
              <textarea
                maxLength={500}
                rows={3}
                className="rounded-s border border-line bg-canvas-2 px-3 py-2 text-sm"
                value={kommentar}
                onChange={(e) => setKommentar(e.target.value)}
                placeholder={t("fejl.kommentarPh")}
              />
            </label>

            {state === "error" && <p className="mt-3 text-sm text-danger">{t("fejl.sendFejl")}</p>}
            <div className="mt-4 flex items-center gap-3">
              <button type="button" className="btn btn-primary" disabled={state === "sending"} onClick={send}>
                {state === "sending" ? t("fejl.sender") : t("fejl.send")}
              </button>
              <button type="button" className="btn btn-ghost" onClick={onClose}>{t("fejl.annuller")}</button>
            </div>
            <p className="mt-3 font-mono text-[11px] text-ink-faint">{t("fejl.anonym")}</p>
          </>
        )}
      </div>
    </div>
  );
}
